import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Popper, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@helpers/translate";
import { useCountries } from "../../../MyProfile/components/ProfileTab/hooks/useCountries";
import {
  findCountryByAlpha2,
  getCountryName,
  toAlpha2CountryCode,
} from "@helpers/countryHelpers";
import { USER } from "../../../../../constants";
import config from "../../../../../config";
import { changeLocale } from "../../../../../actions/localization/changeLocale";
import { useNavigate } from "react-router-dom";

import { getLanguage } from "@constants/languages";
import "./index.sass";

const getCountryLabel = (option, locale, countries) => {
  if (!option) return "";

  if (typeof option === "string") {
    const countryItem =
      findCountryByAlpha2(option, countries) ||
      countries.find((country) => country.code === option);

    if (!countryItem) return "";

    return getCountryName(countryItem, locale, countries);
  }

  return getCountryName(option, locale, countries);
};

const normalizeSearchValue = (value) => {
  return String(value || "")
    .toLowerCase()
    .trim();
};

const filterCountryOptions = (options, { inputValue }, locale) => {
  const searchValue = normalizeSearchValue(inputValue);

  if (!searchValue) return options;

  return options.filter((country) => {
    const searchableValues = [
      getCountryName(country, locale, options),
      country.label_en,
      country.label_ru,
      country.code,
      country.alpha2,
      country.alpha3,
    ];

    return searchableValues.some((value) =>
      normalizeSearchValue(value).includes(searchValue),
    );
  });
};

const LanguagePlatformIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.25 2C6.66563 2 7 2.33437 7 2.75V4.5H11.25C11.6656 4.5 12 4.83437 12 5.25C12 5.66563 11.6656 6 11.25 6H10.7437L10.2281 7.2C9.64687 8.55937 8.81562 9.7875 7.79375 10.825C8.22812 11.1125 8.6875 11.3687 9.16875 11.5875L11.0719 12.4594L13.3156 7.44375C13.4375 7.175 13.7031 7 14 7C14.2969 7 14.5625 7.175 14.6844 7.44375L18.9344 16.9438C19.1031 17.3219 18.9344 17.7656 18.5562 17.9344C18.1781 18.1031 17.7344 17.9344 17.5656 17.5562L16.6469 15.5H11.3562L10.4375 17.5562C10.2688 17.9344 9.825 18.1031 9.44687 17.9344C9.06875 17.7656 8.9 17.3219 9.06875 16.9438L10.4625 13.8281L8.54688 12.95C7.875 12.6406 7.2375 12.2719 6.64375 11.85C6.09688 12.275 5.50937 12.6531 4.8875 12.9781L3.09687 13.9156C2.73125 14.1094 2.27812 13.9656 2.08437 13.6C1.89062 13.2344 2.03438 12.7812 2.4 12.5875L4.1875 11.65C4.625 11.4219 5.04375 11.1594 5.44063 10.875C4.58125 10.075 3.84375 9.15 3.25312 8.125C3.04688 7.76562 3.16875 7.30625 3.52813 7.1C3.8875 6.89375 4.34687 7.01562 4.55312 7.375C5.09687 8.32188 5.7875 9.17188 6.59688 9.89688C7.55 8.96563 8.31875 7.85 8.85312 6.60625L9.1125 6H1.75C1.33437 6 1 5.66563 1 5.25C1 4.83437 1.3375 4.5 1.75 4.5H5.5V2.75C5.5 2.33437 5.83437 2 6.25 2ZM15.975 14L14 9.5875L12.025 14H15.975Z"
      fill="white"
    />
  </svg>
);

const SelectArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99954 11.3332C7.82887 11.3332 7.65818 11.2679 7.52818 11.1379L2.86152 6.47122C2.60085 6.21055 2.60085 5.78917 2.86152 5.52851C3.12218 5.26784 3.54356 5.26784 3.80422 5.52851L7.99954 9.72382L12.1948 5.52851C12.4555 5.26784 12.8769 5.26784 13.1376 5.52851C13.3982 5.78917 13.3982 6.21055 13.1376 6.47122L8.47089 11.1379C8.34089 11.2679 8.1702 11.3332 7.99954 11.3332Z"
      fill="white"
    />
  </svg>
);

const CustomPopper = (props) => {
  const selectWrapper = props.anchorEl?.closest(
    ".futures-onboarding__select--autocomplete",
  );

  const anchorRect = props.anchorEl?.getBoundingClientRect();
  const wrapperRect = selectWrapper?.getBoundingClientRect();

  const offsetX =
    anchorRect && wrapperRect ? wrapperRect.left - anchorRect.left : 0;

  return (
    <Popper
      {...props}
      className={`${
        props.className || ""
      } bo-select-content futures-onboarding__autocomplete-popper`}
      placement="bottom-start"
      modifiers={[
        ...(props.modifiers || []),
        {
          name: "offset",
          options: {
            offset: [offsetX, 6],
          },
        },
      ]}
      style={{
        ...props.style,
        width:
          selectWrapper?.clientWidth ||
          props.anchorEl?.clientWidth ||
          props.style?.width,
      }}
    />
  );
};

const FuturesOnboarding = ({ onStartTutorial, onClose }) => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const locale = useSelector((state) => state.localization.locale);

  const { countries = [] } = useCountries(locale) || {};
  const languages = config.supported_locales;
  const languageDropdownRef = useRef(null);

  const publicUrl = process.env.PUBLIC_URL || "";
  const preferencesBg = `${publicUrl}/images/account-preferences-bg.png`;
  const guideBg = `${publicUrl}/images/guide-bg.png`;

  const [isOpen, setIsOpen] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const isConfirmDisabled = !selectedCountry;

  const selectedCountryItem = findCountryByAlpha2(selectedCountry, countries);

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
      overscrollBehavior: body.style.overscrollBehavior,
    };

    const previousHtmlStyles = {
      overflow: documentElement.style.overflow,
      overscrollBehavior: documentElement.style.overscrollBehavior,
    };

    body.classList.add("futures-onboarding-scroll-lock");
    documentElement.classList.add("futures-onboarding-scroll-lock");

    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.classList.remove("futures-onboarding-scroll-lock");
      documentElement.classList.remove("futures-onboarding-scroll-lock");

      documentElement.style.overflow = previousHtmlStyles.overflow;
      documentElement.style.overscrollBehavior =
        previousHtmlStyles.overscrollBehavior;

      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.overflow = previousBodyStyles.overflow;
      body.style.paddingRight = previousBodyStyles.paddingRight;
      body.style.overscrollBehavior = previousBodyStyles.overscrollBehavior;

      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setIsLanguageOpen(false);
      }
    };

    if (isLanguageOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const countryCode = toAlpha2CountryCode(selectedCountry, countries);

    if (countryCode) {
      dispatch({
        type: USER.USER_FIELD,
        payload: {
          country: countryCode,
        },
      });
    }

    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleTutorial = () => {
    setIsOpen(false);
    onStartTutorial?.();
  };

  const handleDeposit = () => {
    setIsOpen(false);
    onClose?.();
    navigate("/wallet/deposit");
  };

  const handleStart = () => {
    setIsOpen(false);
    onClose?.();
    navigate("/");
  };

  return (
    <div className="futures-onboarding">
      <div className="futures-onboarding__overlay" />

      <div
        className={`futures-onboarding__modal futures-onboarding__modal--step-${step}`}
      >
        {step === 1 && (
          <>
            <button
              type="button"
              className="futures-onboarding__close"
              onClick={handleClose}
              aria-label={__("futures.onboarding.closeLabel")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.3532 11.6472C12.5485 11.8425 12.5485 12.1592 12.3532 12.3545C12.2558 12.4518 12.1278 12.5012 11.9998 12.5012C11.8718 12.5012 11.7438 12.4525 11.6465 12.3545L7.99983 8.70782L4.35317 12.3545C4.25583 12.4518 4.12783 12.5012 3.99983 12.5012C3.87183 12.5012 3.74383 12.4525 3.6465 12.3545C3.45117 12.1592 3.45117 11.8425 3.6465 11.6472L7.29317 8.0005L3.6465 4.35386C3.45117 4.15852 3.45117 3.84183 3.6465 3.6465C3.84183 3.45117 4.1585 3.45117 4.35384 3.6465L8.0005 7.29319L11.6472 3.6465C11.8425 3.45117 12.1592 3.45117 12.3545 3.6465C12.5498 3.84183 12.5498 4.15852 12.3545 4.35386L8.70783 8.0005L12.3532 11.6472Z"
                  fill="white"
                />
              </svg>
            </button>

            <div
              className="futures-onboarding__hero futures-onboarding__hero--preferences"
              style={{ backgroundImage: `url(${preferencesBg})` }}
            />

            <div className="futures-onboarding__content">
              <h2 className="futures-onboarding__title">
                {__("futures.onboarding.step1.title")}
              </h2>

              <p className="futures-onboarding__description">
                {__("futures.onboarding.step1.description")}
              </p>

              <div className="futures-onboarding__field">
                <label className="futures-onboarding__label">
                  {__("futures.onboarding.step1.countryLabel")}
                </label>

                <div className="futures-onboarding__select futures-onboarding__select--autocomplete">
                  <span className="futures-onboarding__select-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.99935 1.04199C5.05935 1.04199 1.04102 5.06116 1.04102 10.0003C1.04102 14.9395 5.05935 18.9587 9.99935 18.9587C14.9393 18.9587 18.9577 14.9395 18.9577 10.0003C18.9577 5.06116 14.9393 1.04199 9.99935 1.04199ZM17.676 9.37533H14.301C14.1918 7.08949 13.4885 4.76033 12.2718 2.6345C15.2243 3.54783 17.4185 6.19199 17.676 9.37533ZM10.6077 2.32281C12.071 4.49865 12.9235 6.96699 13.051 9.37533H6.94769C7.07436 6.96699 7.92767 4.49865 9.39101 2.32281C9.59267 2.30698 9.79435 2.29199 9.99935 2.29199C10.2043 2.29199 10.4069 2.30698 10.6077 2.32281ZM7.72686 2.6345C6.51019 4.76033 5.80686 7.08949 5.69769 9.37533H2.32268C2.58018 6.19199 4.77436 3.54783 7.72686 2.6345ZM2.32268 10.6253H5.69769C5.80686 12.9112 6.51019 15.2403 7.72686 17.3661C4.77436 16.4528 2.58018 13.8087 2.32268 10.6253ZM9.39101 17.6778C7.92767 15.502 7.07519 13.0337 6.94769 10.6253H13.051C12.9243 13.0337 12.071 15.502 10.6077 17.6778C10.406 17.6937 10.2043 17.7087 9.99935 17.7087C9.79435 17.7087 9.59184 17.6937 9.39101 17.6778ZM12.2718 17.3661C13.4885 15.2403 14.1918 12.9112 14.301 10.6253H17.676C17.4185 13.8087 15.2243 16.4528 12.2718 17.3661Z"
                        fill="white"
                      />
                    </svg>
                  </span>

                  <Autocomplete
                    options={countries}
                    getOptionLabel={(option) =>
                      getCountryLabel(option, locale, countries)
                    }
                    filterOptions={(options, state) =>
                      filterCountryOptions(options, state, locale)
                    }
                    value={selectedCountryItem || null}
                    onChange={(event, newValue) => {
                      const countryCode = toAlpha2CountryCode(
                        newValue,
                        countries,
                      );

                      setSelectedCountry(countryCode || "");
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        toAlpha2CountryCode(option, countries) ===
                        toAlpha2CountryCode(value, countries)
                      );
                    }}
                    openOnFocus
                    autoHighlight
                    disableClearable
                    className="futures-onboarding__autocomplete"
                    popupIcon={<SelectArrowIcon />}
                    selectOnFocus
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        className="futures-onboarding__autocomplete-input"
                        placeholder={__(
                          "futures.onboarding.step1.countryPlaceholder",
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "new-password",
                        }}
                      />
                    )}
                    PopperComponent={CustomPopper}
                  />
                </div>
              </div>

              <div className="futures-onboarding__field">
                <label className="futures-onboarding__label">
                  {__("futures.onboarding.step1.languageLabel")}
                </label>

                <div
                  className="futures-onboarding__language-select"
                  ref={languageDropdownRef}
                >
                  <button
                    type="button"
                    className={`futures-onboarding__select futures-onboarding__language-trigger${
                      isLanguageOpen ? " is-open" : ""
                    }`}
                    onClick={() => setIsLanguageOpen((prev) => !prev)}
                  >
                    <span className="futures-onboarding__select-icon">
                      <LanguagePlatformIcon />
                    </span>

                    <span className="futures-onboarding__language-value">
                      {getLanguage(locale).name}
                    </span>

                    <span className="futures-onboarding__select-arrow">
                      <SelectArrowIcon />
                    </span>
                  </button>

                  {isLanguageOpen && (
                    <div className="futures-onboarding__language-menu">
                      {languages.map((lang) => {
                        const ItemFlag = getLanguage(lang).Flag;

                        if (!ItemFlag) return null;

                        return (
                          <button
                            key={lang}
                            type="button"
                            className={`futures-onboarding__language-option ${
                              locale === lang ? "active" : ""
                            }`}
                            onClick={() => {
                              dispatch(changeLocale({ locale: lang }));
                              setIsLanguageOpen(false);
                            }}
                          >
                            <ItemFlag
                              className="futures-onboarding__language-flag"
                              style={{ borderRadius: "2.5px" }}
                            />
                            <span>{getLanguage(lang).name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <p className="futures-onboarding__note">
                {__("futures.onboarding.step1.note")}
              </p>

              <button
                type="button"
                className={`futures-onboarding__button futures-onboarding__button--primary ${
                  isConfirmDisabled
                    ? "futures-onboarding__button--disabled"
                    : ""
                }`}
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
              >
                {__("futures.onboarding.step1.confirm")}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="futures-onboarding__top">
              <button
                type="button"
                className="futures-onboarding__back"
                onClick={handleBack}
              >
                <span className="futures-onboarding__back-icon">
                  <svg
                    width="6"
                    height="11"
                    viewBox="0 0 6 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.16652 10.3338C5.03852 10.3338 4.9105 10.2852 4.81317 10.1872L0.1465 5.52052C-0.0488333 5.32519 -0.0488333 5.0085 0.1465 4.81317L4.81317 0.1465C5.0085 -0.0488333 5.32519 -0.0488333 5.52052 0.1465C5.71586 0.341833 5.71586 0.658523 5.52052 0.853857L1.20721 5.16717L5.52052 9.48048C5.71586 9.67582 5.71586 9.99251 5.52052 10.1878C5.42252 10.2852 5.29452 10.3338 5.16652 10.3338Z"
                      fill="#FCFCFD"
                    />
                  </svg>
                </span>
                <span>{__("futures.onboarding.step2.back")}</span>
              </button>

              <button
                type="button"
                className="futures-onboarding__close futures-onboarding__close--static"
                onClick={handleClose}
                aria-label={__("futures.onboarding.closeLabel")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.3532 11.6472C12.5485 11.8425 12.5485 12.1592 12.3532 12.3545C12.2558 12.4518 12.1278 12.5012 11.9998 12.5012C11.8718 12.5012 11.7438 12.4525 11.6465 12.3545L7.99983 8.70782L4.35317 12.3545C4.25583 12.4518 4.12783 12.5012 3.99983 12.5012C3.87183 12.5012 3.74383 12.4525 3.6465 12.3545C3.45117 12.1592 3.45117 11.8425 3.6465 11.6472L7.29317 8.0005L3.6465 4.35386C3.45117 4.15852 3.45117 3.84183 3.6465 3.6465C3.84183 3.45117 4.1585 3.45117 4.35384 3.6465L8.0005 7.29319L11.6472 3.6465C11.8425 3.45117 12.1592 3.45117 12.3545 3.6465C12.5498 3.84183 12.5498 4.15852 12.3545 4.35386L8.70783 8.0005L12.3532 11.6472Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>

            <div
              className="futures-onboarding__hero futures-onboarding__hero--guide"
              style={{ backgroundImage: `url(${guideBg})` }}
            />

            <div className="futures-onboarding__content futures-onboarding__content--guide">
              <h2 className="futures-onboarding__title">
                {__("futures.onboarding.step2.title")}
              </h2>

              <p className="futures-onboarding__description">
                {__("futures.onboarding.step2.description")} <span>BINIFY</span>
              </p>

              <div className="futures-onboarding__guide-card">
                <div className="futures-onboarding__guide-main">
                  <div className="futures-onboarding__card-icon">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M24.5419 4.87941C24.5407 4.87941 24.5408 4.87944 24.5396 4.87827C22.5539 3.93677 20.3526 3.60659 17.9959 3.89475C16.2996 4.10359 14.8645 5.07303 14 6.43103C13.1343 5.0742 11.7004 4.10359 10.0041 3.89475C7.64394 3.60659 5.44491 3.93677 3.46041 4.87827C3.46041 4.87827 3.46044 4.87827 3.45927 4.87827C2.95294 5.11861 2.625 5.64001 2.625 6.20701V21.3795C2.625 21.8392 2.83491 22.2604 3.20007 22.5369C3.56407 22.8134 4.04961 22.9008 4.49178 22.7772C7.88445 21.8135 10.7532 22.2219 13.5135 24.0618C13.5252 24.0699 13.5392 24.0723 13.5508 24.0793C13.5637 24.0863 13.573 24.0979 13.587 24.1049C13.5998 24.1119 13.6149 24.1085 13.6277 24.1155C13.7456 24.1715 13.8705 24.2087 13.9989 24.2087C14.1272 24.2087 14.2519 24.1715 14.3697 24.1155C14.3825 24.1096 14.3979 24.1119 14.4107 24.1049C14.4236 24.0979 14.4341 24.0874 14.4458 24.0793C14.4574 24.0723 14.4714 24.0699 14.4831 24.0618C17.2434 22.2208 20.111 21.8124 23.5048 22.776C23.9481 22.902 24.4311 22.8134 24.7962 22.5369C25.1614 22.2616 25.3716 21.8392 25.3716 21.3795V6.20701C25.3751 5.64234 25.0482 5.12091 24.5419 4.87941ZM7.85506 20.5256C6.73389 20.5256 5.5755 20.6831 4.375 20.9946V6.38204C6.03167 5.63187 7.8086 5.3858 9.79077 5.6308C11.6924 5.86413 13.125 7.48572 13.125 9.40139V21.8042C11.4508 20.9514 9.70306 20.5256 7.85506 20.5256ZM23.625 20.9957C20.4423 20.1685 17.5653 20.4335 14.875 21.8066V9.40381C14.875 7.48697 16.3087 5.86641 18.2092 5.63308C18.7016 5.57241 19.1812 5.54207 19.649 5.54207C21.0665 5.54207 22.379 5.82096 23.6239 6.38446V20.9957H23.625ZM17.5 9.62541H21C21.483 9.62541 21.875 10.0174 21.875 10.5004C21.875 10.9834 21.483 11.3754 21 11.3754H17.5C17.017 11.3754 16.625 10.9834 16.625 10.5004C16.625 10.0174 17.017 9.62541 17.5 9.62541ZM20.7083 14.0004C20.7083 14.4834 20.3163 14.8754 19.8333 14.8754H17.5C17.017 14.8754 16.625 14.4834 16.625 14.0004C16.625 13.5174 17.017 13.1254 17.5 13.1254H19.8333C20.3163 13.1254 20.7083 13.5174 20.7083 14.0004Z"
                        fill="#FF6C22"
                      />
                    </svg>
                  </div>

                  <div className="futures-onboarding__card-content">
                    <h3>{__("futures.onboarding.step2.guideTitle")}</h3>
                    <p>{__("futures.onboarding.step2.guideDescription")}</p>
                  </div>
                </div>

                <button
                  type="button"
                  className="futures-onboarding__button futures-onboarding__button--tutorial"
                  onClick={handleTutorial}
                >
                  {__("futures.onboarding.step2.tutorial")}
                </button>
              </div>

              <div className="futures-onboarding__actions-grid">
                <div className="futures-onboarding__action-card">
                  <div className="futures-onboarding__card-icon">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.875 7.3465V7C21.875 4.179 20.321 2.625 17.5 2.625H5.83333C4.0635 2.625 2.625 4.0635 2.625 5.83333V21C2.625 23.821 4.179 25.375 7 25.375H21C23.821 25.375 25.375 23.821 25.375 21V11.6667C25.375 9.1525 24.1407 7.64516 21.875 7.3465ZM23.625 18.375H19.25C18.1242 18.375 17.2083 17.4592 17.2083 16.3333C17.2083 15.2075 18.1242 14.2917 19.25 14.2917H23.625V18.375ZM5.83333 4.375H17.5C19.3398 4.375 20.125 5.16017 20.125 7V7.29167H5.83333C5.0295 7.29167 4.375 6.63717 4.375 5.83333C4.375 5.0295 5.0295 4.375 5.83333 4.375ZM21 23.625H7C5.16017 23.625 4.375 22.8398 4.375 21V8.69047C4.8125 8.91447 5.30833 9.04167 5.83333 9.04167H21C22.8398 9.04167 23.625 9.82683 23.625 11.6667V12.5417H19.25C17.1593 12.5417 15.4583 14.2427 15.4583 16.3333C15.4583 18.424 17.1593 20.125 19.25 20.125H23.625V21C23.625 22.8398 22.8398 23.625 21 23.625ZM19.845 15.1667H19.8567C20.5019 15.1667 21.0234 15.6893 21.0234 16.3333C21.0234 16.9773 20.5019 17.5 19.8567 17.5C19.2127 17.5 18.6842 16.9773 18.6842 16.3333C18.6842 15.6893 19.201 15.1667 19.845 15.1667Z"
                        fill="#FF6C22"
                      />
                    </svg>
                  </div>
                  <h3>{__("futures.onboarding.step2.depositTitle")}</h3>
                  <p>{__("futures.onboarding.step2.depositDescription")}</p>

                  <button
                    type="button"
                    className="futures-onboarding__button futures-onboarding__button--outline"
                    onClick={handleDeposit}
                  >
                    {__("futures.onboarding.step2.depositButton")}{" "}
                    <svg
                      width="6"
                      height="11"
                      viewBox="0 0 6 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.499853 10.3345C0.371853 10.3345 0.243833 10.2858 0.1465 10.1878C-0.0488333 9.99251 -0.0488333 9.67582 0.1465 9.48048L4.45981 5.16717L0.1465 0.853857C-0.0488333 0.658523 -0.0488333 0.341833 0.1465 0.1465C0.341833 -0.0488333 0.658523 -0.0488333 0.853857 0.1465L5.52052 4.81317C5.71586 5.0085 5.71586 5.32519 5.52052 5.52052L0.853857 10.1872C0.755857 10.2859 0.627853 10.3345 0.499853 10.3345Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>

                <div className="futures-onboarding__action-card">
                  <div className="futures-onboarding__card-icon">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M25.375 23.3338C25.375 23.8168 24.983 24.2088 24.5 24.2088H5.25C3.60617 24.2088 2.625 23.2277 2.625 21.5838V4.66716C2.625 4.18415 3.017 3.79215 3.5 3.79215C3.983 3.79215 4.375 4.18415 4.375 4.66716V21.5838C4.375 22.271 4.56283 22.4588 5.25 22.4588H24.5C24.983 22.4588 25.375 22.8508 25.375 23.3338ZM14.2917 14.0005V18.6672C14.2917 19.1502 14.6837 19.5422 15.1667 19.5422C15.6497 19.5422 16.0417 19.1502 16.0417 18.6672V14.0005C16.0417 13.5175 15.6497 13.1255 15.1667 13.1255C14.6837 13.1255 14.2917 13.5175 14.2917 14.0005ZM21 10.7922C20.517 10.7922 20.125 11.1842 20.125 11.6672V18.6672C20.125 19.1502 20.517 19.5422 21 19.5422C21.483 19.5422 21.875 19.1502 21.875 18.6672V11.6672C21.875 11.1842 21.483 10.7922 21 10.7922ZM9.33333 19.5422C9.81633 19.5422 10.2083 19.1502 10.2083 18.6672V16.3338C10.2083 15.8508 9.81633 15.4588 9.33333 15.4588C8.85033 15.4588 8.45833 15.8508 8.45833 16.3338V18.6672C8.45833 19.1502 8.85033 19.5422 9.33333 19.5422ZM9.33333 10.3839C9.55733 10.3839 9.78133 10.2987 9.95166 10.1272L11.9058 8.17299C12.1252 7.95366 12.5195 7.95366 12.74 8.17299L14.0817 9.51469C14.994 10.4282 16.4792 10.427 17.3892 9.51469L20.125 6.77889V8.16716C20.125 8.65016 20.517 9.04216 21 9.04216C21.483 9.04216 21.875 8.65016 21.875 8.16716V4.66716C21.875 4.55282 21.8517 4.43967 21.8073 4.33234C21.7186 4.11884 21.5483 3.9485 21.3348 3.85866C21.2275 3.81433 21.1143 3.79102 21 3.79102H17.5C17.017 3.79102 16.625 4.18302 16.625 4.66602C16.625 5.14902 17.017 5.54102 17.5 5.54102H18.8872L16.1513 8.27681C15.925 8.50315 15.5435 8.50198 15.3183 8.27681L13.9767 6.93512C13.0643 6.02279 11.5803 6.02279 10.668 6.93512L8.71383 8.88934C8.37199 9.23118 8.37199 9.78538 8.71383 10.1272C8.88533 10.2987 9.10933 10.3839 9.33333 10.3839Z"
                        fill="#FF6C22"
                      />
                    </svg>
                  </div>
                  <h3>{__("futures.onboarding.step2.startTitle")}</h3>
                  <p>{__("futures.onboarding.step2.startDescription")}</p>

                  <button
                    type="button"
                    className="futures-onboarding__button futures-onboarding__button--outline"
                    onClick={handleStart}
                  >
                    {__("futures.onboarding.step2.startButton")}{" "}
                    <svg
                      width="6"
                      height="11"
                      viewBox="0 0 6 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.499853 10.3345C0.371853 10.3345 0.243833 10.2858 0.1465 10.1878C-0.0488333 9.99251 -0.0488333 9.67582 0.1465 9.48048L4.45981 5.16717L0.1465 0.853857C-0.0488333 0.658523 -0.0488333 0.341833 0.1465 0.1465C0.341833 -0.0488333 0.658523 -0.0488333 0.853857 0.1465L5.52052 4.81317C5.71586 5.0085 5.71586 5.32519 5.52052 5.52052L0.853857 10.1872C0.755857 10.2859 0.627853 10.3345 0.499853 10.3345Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <p className="futures-onboarding__footer-text">
                {__("futures.onboarding.step2.footer")} <span>BINIFY</span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FuturesOnboarding;
